// ─────────────────────────────────────────────────────────────────────────────
// controlTab.ts (Training)
//
// Mirrors the desktop Training control panel's "control" surface:
//   • Active timer card with title and remaining clock.
//   • Transport row: Start/Pause, Skip, Reset active.
//   • Queue list with per-row remove + reorder, and a clear-all action.
// ─────────────────────────────────────────────────────────────────────────────

import { h } from "../../../ui/dom.js";
import { fireTraining, EventTypes } from "../fire.js";
import { safeText, safeMinSec, intOr, EM_DASH } from "../../../lib/safe.js";
import type { LiveState, TrainingTimer } from "../../../contract/liveState.js";

export function controlTab(sessionId: string, state: LiveState): HTMLElement {
	return h(
		"div",
		{ class: "space-y-3" },
		activeCard(sessionId, state),
		transportCard(sessionId, state),
		queueCard(sessionId, state)
	);
}

function activeCard(_sessionId: string, state: LiveState): HTMLElement {
	const t = state.training;
	const hasActive = t.activeIndex >= 0 && t.activeTitle.length > 0;
	const title = hasActive ? safeText(t.activeTitle, "Active timer") : "No active timer";
	const remaining = safeMinSec(t.activeRemainingSeconds);
	const total = safeMinSec(t.activeTotalSeconds);

	return h(
		"section",
		{ class: "card space-y-2" },
		h("div", { class: "field-label" }, "Active timer"),
		h("div", { class: "truncate text-base font-bold text-slate-200" }, title),
		h(
			"div",
			{
				class: "text-center font-mono text-6xl font-black numeric-hero",
				style: "color:var(--accent)",
			},
			remaining
		),
		h(
			"div",
			{ class: "text-center text-[11px] uppercase tracking-wide text-slate-400" },
			hasActive ? `Total ${total}` : "Add a timer below to begin"
		)
	);
}

function transportCard(sessionId: string, state: LiveState): HTMLElement {
	const running = state.training.isRunning === true;
	return h(
		"section",
		{ class: "card space-y-2" },
		h("div", { class: "field-label" }, "Transport"),
		h(
			"div",
			{ class: "grid grid-cols-3 gap-2" },
			h(
				"button",
				{
					type: "button",
					class: running ? "btn-danger" : "btn-success",
					onclick: () =>
						fireTraining(
							sessionId,
							EventTypes.TrainingStartPause,
							running ? "Paused" : "Started"
						),
				},
				running ? "⏸ Pause" : "▶ Start"
			),
			h(
				"button",
				{
					type: "button",
					class: "btn-ghost",
					onclick: () => fireTraining(sessionId, EventTypes.TrainingSkip, "Skipped"),
				},
				"⏭ Skip"
			),
			h(
				"button",
				{
					type: "button",
					class: "btn-warn",
					onclick: () =>
						fireTraining(sessionId, EventTypes.TrainingResetActive, "Reset active timer"),
				},
				"↺ Reset"
			)
		)
	);
}

function queueCard(sessionId: string, state: LiveState): HTMLElement {
	const timers = state.training.timers;
	const activeIndex = intOr(state.training.activeIndex, -1);

	const list = h(
		"ul",
		{ class: "divide-y divide-ink-400/60" },
		...(timers.length === 0
			? [h("li", { class: "py-3 text-sm text-slate-500" }, "Queue is empty")]
			: timers.map((timer, idx) => queueRow(sessionId, timer, idx, idx === activeIndex)))
	);

	return h(
		"section",
		{ class: "card space-y-2" },
		h(
			"div",
			{ class: "flex items-center justify-between" },
			h("div", { class: "field-label" }, `Queue (${timers.length})`),
			timers.length > 0
				? h(
					"button",
					{
						type: "button",
						class: "btn-ghost px-3 py-1 text-xs",
						onclick: () => {
							if (!confirm("Clear all timers?")) return;
							void fireTraining(sessionId, EventTypes.TrainingClearQueue, "Queue cleared");
						},
					},
					"Clear all"
				)
				: null
		),
		list
	);
}

function queueRow(
	sessionId: string,
	timer: TrainingTimer,
	index: number,
	isActive: boolean
): HTMLElement {
	const title = safeText(timer.title, "Timer");
	const total = safeMinSec(timer.totalSeconds);
	const remaining = safeMinSec(timer.remainingSeconds);
	const finished = timer.hasFinished === true;
	const id = safeText(timer.id, EM_DASH);

	return h(
		"li",
		{
			class: `flex items-center gap-2 py-2 text-sm ${
				isActive ? "rounded-lg bg-ink-700/60 px-1" : ""
			}`,
		},
		h(
			"div",
			{ class: "min-w-0 flex-1" },
			h(
				"div",
				{ class: "flex items-center gap-2" },
				isActive
					? h(
						"span",
						{
							class: "pill",
							style: "background:var(--accent-dim);color:var(--accent)",
						},
						"NOW"
					)
					: null,
				h(
					"span",
					{
						class: `truncate ${finished ? "text-slate-500 line-through" : "text-slate-200"}`,
					},
					title
				)
			),
			h(
				"div",
				{ class: "text-[11px] text-slate-500" },
				`${remaining} / ${total}`
			)
		),
		h(
			"div",
			{ class: "flex shrink-0 items-center gap-1" },
			h(
				"button",
				{
					type: "button",
					class: "chip",
					title: "Move up",
					onclick: () =>
						fireTraining(sessionId, EventTypes.TrainingReorder, "Reordered", {
							id,
							direction: "up",
						}),
				},
				"▲"
			),
			h(
				"button",
				{
					type: "button",
					class: "chip",
					title: "Move down",
					onclick: () =>
						fireTraining(sessionId, EventTypes.TrainingReorder, "Reordered", {
							id,
							direction: "down",
						}),
				},
				"▼"
			),
			h(
				"button",
				{
					type: "button",
					class: "chip text-accent-red",
					title: "Remove",
					onclick: () =>
						fireTraining(sessionId, EventTypes.TrainingRemoveTimer, "Removed", { id }),
				},
				"✕"
			)
		)
	);
}
